import { Calendar, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineClose, AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import styled from 'styled-components';

const MAX_DAYS = 99;
const MAX_VISIBLE_CHIPS = 6;

const CalendarShell = styled.div`
  .ant-picker-calendar .ant-picker-panel {
    border-top: none;
  }
  .ant-picker-cell {
    padding: 2px 0 !important;
  }
  .ant-picker-cell-disabled::before {
    background: transparent;
  }
  .ant-picker-content th {
    color: #9ca3af;
    font-size: 12px;
    font-weight: 500;
    padding-bottom: 4px;
  }
`;

interface MultiDateCalendarProps {
  dates: string[];
  onToggleDate: (date: string) => void;
  onRemoveDate: (date: string) => void;
}

/**
 * Flex-mode day picker: tap days on an inline calendar to toggle them.
 * Selection lives in dayPassInfo.dates (the hook state) — this component
 * only renders it and reports toggles, so back-navigation restores
 * selection for free.
 */
const MultiDateCalendar: FC<MultiDateCalendarProps> = ({ dates, onToggleDate, onRemoveDate }) => {
  const { t } = useTranslation();
  const [showAllChips, setShowAllChips] = React.useState(false);
  const selectedSet = useMemo(() => new Set(dates), [dates]);
  const sortedDates = useMemo(() => [...dates].sort(), [dates]);

  const handleSelect = (date: Dayjs, info: { source: string }) => {
    // Month navigation also fires onSelect — only direct date taps toggle.
    if (info.source !== 'date') return;
    if (date.isBefore(dayjs().endOf('day'))) return;
    const key = date.format('YYYY-MM-DD');
    if (!selectedSet.has(key) && dates.length >= MAX_DAYS) {
      notification.warning({ message: t('dayPass.maxDaysReached') });
      return;
    }
    if (selectedSet.has(key) && dates.length === 1) return;
    onToggleDate(key);
  };

  const visibleChips = showAllChips ? sortedDates : sortedDates.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = sortedDates.length - visibleChips.length;

  return (
    <div>
      {/* Count row */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium" style={{ color: '#404D61' }}>
          {t('dayPass.selectDays')}
        </span>
        <span className="text-sm font-semibold" style={{ color: '#404D61' }}>
          {t('dayPass.daysSelected', { count: dates.length })}
        </span>
      </div>

      <CalendarShell className="rounded-xl p-2" style={{ border: '1px solid #E5E7EB' }}>
        <Calendar
          fullscreen={false}
          disabledDate={(d) => d.isBefore(dayjs().endOf('day'))}
          onSelect={handleSelect}
          headerRender={({ value, onChange }) => (
            <div className="flex items-center justify-between px-2 py-1.5">
              <button
                type="button"
                aria-label={t('dayPass.previousMonth')}
                className="flex items-center justify-center w-8 h-8 rounded-lg border-none cursor-pointer bg-transparent hover:bg-[#F3F4F6]"
                onClick={() => onChange(value.subtract(1, 'month'))}
              >
                <AiOutlineLeft size={16} className="rtl:rotate-180" style={{ color: '#707070' }} />
              </button>
              <span className="text-sm font-semibold" style={{ color: '#404D61' }}>
                {value.format('MMMM YYYY')}
              </span>
              <button
                type="button"
                aria-label={t('dayPass.nextMonth')}
                className="flex items-center justify-center w-8 h-8 rounded-lg border-none cursor-pointer bg-transparent hover:bg-[#F3F4F6]"
                onClick={() => onChange(value.add(1, 'month'))}
              >
                <AiOutlineRight size={16} className="rtl:rotate-180" style={{ color: '#707070' }} />
              </button>
            </div>
          )}
          fullCellRender={(date: Dayjs, info) => {
            if (info.type !== 'date') return info.originNode;
            const key = date.format('YYYY-MM-DD');
            const isSelected = selectedSet.has(key);
            const isDisabled = date.isBefore(dayjs().endOf('day'));
            const isToday = date.isSame(dayjs(), 'day');

            let cellStyle: React.CSSProperties = { color: '#404D61' };
            if (isDisabled) {
              cellStyle = { color: '#D1D5DB', cursor: 'default' };
            } else if (isSelected) {
              cellStyle = { backgroundColor: 'var(--main)', color: '#FFFFFF' };
            } else if (isToday) {
              cellStyle = { border: '1px solid var(--main)', color: '#404D61' };
            }

            return (
              <div
                aria-pressed={isSelected}
                className={`flex items-center justify-center mx-auto rounded-full text-sm w-9 h-9 ${
                  isSelected ? 'font-medium' : ''
                } ${isDisabled ? '' : 'cursor-pointer hover:bg-[#F3F4F6]'}`}
                style={cellStyle}
              >
                {date.date()}
              </div>
            );
          }}
        />
      </CalendarShell>

      {/* Selected-date chips */}
      {sortedDates.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {visibleChips.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 ps-3 pe-2 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB', color: '#404D61' }}
            >
              {dayjs(d).format('ddd, MMM D')}
              {sortedDates.length > 1 && (
                <button
                  type="button"
                  aria-label={t('dayPass.removeDate', { date: dayjs(d).format('MMM D') })}
                  className="flex items-center justify-center border-none bg-transparent cursor-pointer p-0"
                  style={{ color: '#707070' }}
                  onClick={() => onRemoveDate(d)}
                >
                  <AiOutlineClose size={12} />
                </button>
              )}
            </span>
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-none cursor-pointer"
              style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB', color: '#404D61' }}
              onClick={() => setShowAllChips(true)}
            >
              {t('dayPass.moreDates', { count: hiddenCount })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiDateCalendar;
